import React, { useEffect, useRef, useMemo } from "react";

import useTranslations from "../../../../hooks/useTranslations";

import { QuestionFragment, AnswerFragment } from '../../../../graphql/generated/definitions';

import { Textarea, FormEntry, Input } from "../../../wombat-ui";

import {
  getAnswersByRole,
  QuestionImplProps,
  QuestionPreviewImplProps,
} from "../Question";
import QuestionTitle from "./utils/QuestionTitle";
import Answer from "./utils/Answer";
import useViewer from "../../../../hooks/useViewer";
import { Button, VStack } from "@chakra-ui/react";
import QuestionDocuments from "./utils/QuestionDocuments";

type QuestionTextValue = string;

type QuestionText = {
  defaultValue: QuestionTextValue;
} & QuestionFragment;

export function isQuestionText(q: QuestionFragment): q is QuestionText {
  return q.type === "QuestionText";
}

export function parseQuestion(question: QuestionFragment): QuestionText {
  return question as QuestionText;
}

type AnswerText = {
  value: QuestionTextValue;
} & AnswerFragment;

export function parseAnswers(answers: AnswerFragment[]): AnswerText[] {
  return answers as AnswerText[];
}

type QuestionTextProps = QuestionImplProps;

export const QuestionText: React.FC<QuestionTextProps> = React.memo(
  ({
    question: rawQuestion,
    answers: rawAnswers,
    review,
    register,
    errors,
    toggleHistoryModalOpen,
    status,
    displayIndex,
    name,
    needsToBeFilled,
    onReview,
    autoFocus,
  }) => {
    const t = useTranslations();

    const { id: viewerId } = useViewer();
    const question = useMemo(() => parseQuestion(rawQuestion), [rawQuestion]);
    const answers = useMemo(
      () => getAnswersByRole(rawAnswers, parseAnswers, review, viewerId),
      [rawAnswers, viewerId]
    );

    const inputRef = useRef(null);
    useEffect(() => {
      if (autoFocus) inputRef.current?.focus();
    }, [inputRef.current, autoFocus]);

    return (
      <div>
        <FormEntry
          title={
            <QuestionTitle
              displayIndex={displayIndex}
              required={question.required && status === "to_be_filled_collaborator"}
              title={question.label}
              description={question.description}
              referents={question.visibility === 'collaborator' ? []: review.referents}
            />
          }
          description={
            <VStack align="flex-start">
              <QuestionDocuments documents={question.documents} />
              <Button
                onClick={toggleHistoryModalOpen} 
                variant="link"
                size="xs"
                id={`answerHistoryButton-${question.id}`}
              >
                {t("reports.answerHistory")}
              </Button>
            </VStack>
          }
        >
          {needsToBeFilled && (
            <Textarea
              defaultValue={answers?.mine?.[0]?.value}
              label={question.label}
              id={question.id}
              error={errors}
              name={`${name}.value`}
              placeholder={t("reports.textPlaceholder") as string}
              ref={(ref) => {
                inputRef.current = ref;
                register(ref, {
                  required: question.required && status === "to_be_filled_collaborator"
                    ? t("forms.errors.required") as string
                    : false,
                });
              }}
              srOnlyLabel
            />
          )}
        </FormEntry>
        {question.timeTracked && (
          <Input
            label={
              !question.expectedTimeValue
                ? (t("reports.timeSpentInHour") as string)
                : `${t("reports.timeSpentInHour") as string} - ${t(
                    `reports.expectedTime.${question.expectedTimeUnit}`,
                    {
                      count: question.expectedTimeValue,
                    }
                  )}`
            }
            type="number"
            size="small"
            id={`${question.id}-timeTracked`}
            name={`${name}.timeSpent`}
            disabled={!needsToBeFilled}
            ref={register({ valueAsNumber: true })}
            defaultValue={answers?.collaborator?.[0]?.timeSpent || undefined}
          />
        )}
        {(status === "to_be_filled_evaluator" || onReview) && (
          <div className="px-4 py-3 my-2">
            <h4 className="text-sm font-medium leading-5 text-dark-100">
              {t("reports.collaboratorAnswer")}
            </h4>
            <div className="grid grid-cols-1 gap-3 pt-3">
              {answers.collaborator.length > 0 ? (
                answers.collaborator.map((a) => (
                  <Answer answer={a} key={a.id} />
                ))
              ) : (
                <p className="text-xs">{t("reports.noAnswer")}</p>
              )}
            </div>
          </div>
        )}
        {status !== "to_be_filled_evaluator" && answers.referents.length > 0 && (
          <div className="px-4 py-3 my-2">
            <h4 className="text-sm font-medium leading-5 text-dark-100">
              {t("reports.evaluatorsAnswers", {
                count: answers.referents.length,
              })}
            </h4>
            <div className="grid grid-cols-1 gap-3 pt-3">
              {answers.referents.length > 0 ? (
                answers.referents.map((a) => (
                  <Answer answer={a} key={a.id} />
                ))
              ) : (
                <p className="text-xs">{t("reports.noAnswer")}</p>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }
);

type QuestionTextPreviewProps = QuestionPreviewImplProps;

export const QuestionTextPreview: React.FC<QuestionTextPreviewProps> =
  React.memo(({ question: rawQuestion, displayIndex }) => {
    const t = useTranslations();

    const question = useMemo(() => parseQuestion(rawQuestion), [rawQuestion]);

    return (
      <div>
        <FormEntry
          title={
            <QuestionTitle displayIndex={displayIndex} title={question.label} />
          }
          description={t("reports.textQuestion") as string}
        >
          <Textarea
            label={question.label}
            id={question.id}
            srOnlyLabel
            disabled
          />
        </FormEntry>
      </div>
    );
  });
